import actionsList from "actions";
import bgTasksActions from "actions/bgTasks";
import carChatActions from "actions/carChat";
import layoutActions from "actions/layout";
import mapActions from "actions/map";
import modalActions from "actions/modal";
import { OrganizationProfileModel } from "api";
import { isObject } from "helpers/system";
import isEmpty from "lodash/isEmpty";
import policy from "policies";
import { FC } from "react";
import { connect } from "react-redux";
import { Action, ActionFunctionAny, createActions, handleActions } from "redux-actions";
import { load, store } from "./filtersStorage";
import { RolesModel } from "./roles";

type DefaultActionTypes = {
    GET_ITEMS: string;
    SET_LOADING: string;
    MERGE_PARAMS: string;
    REPLACE_PARAMS: string;
    CLEAR_PARAMS: string;
};
type DefaultReducerBody<T> = {
    items: T[];
    params: {};
    count: number;
    maxCount: number;
    isLoadingList: boolean;
    isLoadingCount: boolean;
    shouldUpdate: boolean;
    itemsLoadedAt: number;
};

function generateDefaultActions() {
    const actions = (Object.keys(actionsList) as Array<keyof typeof actionsList>).reduce(
        (acc, key) => {
            const prefix = key
                .split("-")
                .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
                .join("");
            return {
                ...acc,
                [key]: defaultActions(defaultConstants(key.toUpperCase()), actionsList[key] as any, prefix),
            };
        },
        {} as { [key: string]: any },
    );
    actions["car-chats"].readAllCarChats = () => (dispatch: ActionFunctionAny<Action<Object>>) =>
        dispatch(carChatActions.readAllCarChats());
    actions["car-chats"].readCarChat = (params: {}) => (dispatch: ActionFunctionAny<Action<Object>>) =>
        dispatch(carChatActions.readCarChat(params));

    return actions;
}

const generatedActions = generateDefaultActions();

const dateFilters = [
    "lastBidPointPlanEnterDate",
    "firstBidPointPlanEnterDate",
    "lastBidPoint/planEnterDate",
    "firstBidPoint/planEnterDate",
    "lastBidPoint/planEnterDateOffset",
    "firstBidPoint/planEnterDateOffset",
    "lastFixedAt",
    "ge",
    "le",
];

const notStorableKeys = ["suitable-distribution-bids"];

export { connect, handleActions, createActions, generatedActions };

export const deletedFilters = () => ({
    preparedFilter: { and: { isDeleted: false } },
    customFilters: { isDeleted: { filterType: "boolean", id: "isDeleted", value: "false" } },
});

export function defaultConstants(prefix = "") {
    return {
        GET_ITEMS: `GET_${prefix}_LIST`,
        SET_LOADING: `SET_${prefix}_LOADING`,
        MERGE_PARAMS: `MERGE_${prefix}_PARAMS`,
        REPLACE_PARAMS: `REPLACE_${prefix}_PARAMS`,
        CLEAR_PARAMS: `CLEAR_${prefix}_PARAMS`,
    };
}

const formatDateObject = (obj: string | { [key: string]: string | { value: string | Date } | Date }) => {
    if (typeof obj === "string" || obj instanceof String) {
        return new Date(obj as string);
    } else {
        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === "string" && dateFilters.includes(key as string)) {
                obj[key] = new Date(obj[key] as string);
            }
        });
        return obj;
    }
};

const prepareDateValues = (obj: { [key: string]: string | {} }) => {
    (Object.keys(obj) as string[]).forEach((key) => {
        if (dateFilters.includes(key)) {
            obj[key] = formatDateObject(obj[key]);
        }
        if (isObject(obj[key])) {
            prepareDateValues(obj[key] as {});
        }
    });
};

export function defaultReducer<T, S>(
    actionTypes: DefaultActionTypes,
    filterParams: { orderBy?: {}; preparedFilter?: {}; customFilters?: {} } = {},
    prefix = "",
) {
    const { orderBy, preparedFilter, customFilters } = filterParams;
    const storedParams = load(`${prefix}_params`, {});
    prepareDateValues(storedParams.preparedFilter || {});
    prepareDateValues(storedParams.customFilters || {});
    const orderParam = isEmpty(orderBy) ? {} : { orderBy };
    const preparedFilterParam = isEmpty(preparedFilter) ? {} : { preparedFilter };
    const customFiltersParam = isEmpty(customFilters) ? {} : { customFilters };
    const initialState: S = {
        key: prefix,
        items: [],
        params: {
            start: 0,
            length: 30,
            filters: [],
            customFilters: {},
            preparedFilter: {},
            queryFilter: {},
            ...orderParam,
            ...preparedFilterParam,
            ...customFiltersParam,
            ...storedParams,
        },
        count: 0,
        isLoadingList: true,
        isLoadingCount: true,
        shouldUpdate: true,
    } as S;
    return handleActions(defaultReducerBody<T, S>(actionTypes, initialState as S & { params: {} }), initialState);
}

export function defaultReducerBody<T, S>(
    actionTypes: DefaultActionTypes,
    initialState: S & { params: {} },
): { [x: string]: (s: S, payload: any) => S } {
    const { GET_ITEMS, SET_LOADING, MERGE_PARAMS, REPLACE_PARAMS, CLEAR_PARAMS } = actionTypes;
    return {
        [GET_ITEMS]: (
            state: { params: {} },
            { payload }: { payload: { data: Array<T>; count: number; maxCount: number; start: number } },
        ) => ({
            ...state,
            items: payload.data,
            count: payload.count,
            isLoadingList: false,
            isLoadingCount: false,
            shouldUpdate: false,
            maxCount: payload.maxCount,
            itemsLoadedAt: Date.now(),
            ...(payload.start !== undefined ? { params: { ...state.params, start: payload.start } } : {}),
        }),
        [SET_LOADING]: (state: {}, { payload = true }: { payload: {} }) => ({
            ...state,
            isLoadingList: payload,
            isLoadingCount: payload,
            shouldUpdate: false,
        }),
        [MERGE_PARAMS]: (state: { params: {}; key: string }, { payload }: { payload: {} }) => {
            const params = { ...state.params, ...payload };
            if (!notStorableKeys.includes(state.key)) {
                store(`${state.key}_params`, params);
            }
            return {
                ...state,
                maxCount: undefined,
                params,
                shouldUpdate: true,
            };
        },
        [REPLACE_PARAMS]: (state: {}, { payload }: { payload: {} }) => ({
            ...state,
            params: { ...initialState.params, ...payload },
            shouldUpdate: true,
        }),
        [CLEAR_PARAMS]: (state: {}, { payload }: { payload: {} }) => ({
            ...state,
            params: { ...initialState.params, ...payload },
        }),
    } as unknown as { [x: string]: (s: S, payload: any) => S };
}

export function defaultActions<T>(
    actionTypes: DefaultActionTypes,
    req: { list: (params: {}) => Promise<false | Array<T>> },
    prefix: string,
) {
    const { GET_ITEMS, SET_LOADING, MERGE_PARAMS, REPLACE_PARAMS, CLEAR_PARAMS } = actionTypes;
    const actions = createActions(GET_ITEMS, SET_LOADING, MERGE_PARAMS, REPLACE_PARAMS, CLEAR_PARAMS);
    const { list } = req;
    const getList = (params: {}, dispatch: ActionFunctionAny<Action<Object>>) =>
        list(params).then((res) => res !== false && dispatch(actions[`get${prefix}List`](res)));
    const index = (params: {}) => (dispatch: ActionFunctionAny<Action<Object>>) => {
        dispatch(actions[`set${prefix}Loading`]());
        return getList(params, dispatch).then(
            (listRes: false | Action<Object>) => listRes === false && dispatch(actions[`set${prefix}Loading`](false)),
        );
    };
    const merge = (params: {}) => (dispatch: ActionFunctionAny<Action<Object>>) =>
        dispatch(actions[`merge${prefix}Params`](params));
    const replace = (params: {}) => (dispatch: ActionFunctionAny<Action<Object>>) =>
        dispatch(actions[`replace${prefix}Params`](params));
    const clear = (params: {}) => (dispatch: ActionFunctionAny<Action<Object>>) =>
        dispatch(actions[`clear${prefix}Params`](params));

    return { ...actions, index, merge, replace, clear };
}

export function tableStateToProps<T>(
    state: { [key: string]: DefaultReducerBody<T> } & {
        profile: {
            roles: RolesModel;
            profile: { canCreateBids: boolean; isDemoPeriodExpired?: boolean };
            organization: OrganizationProfileModel;
            timezone: string;
            refuelingSettings: {};
        };
        dictionaries: { enums: [] };
        layout: { theme: string };
    },
    reducer: string,
) {
    return {
        items: state[reducer].items,
        params: state[reducer].params,
        count: state[reducer].count,
        maxCount: state[reducer].maxCount,
        isLoadingList: state[reducer].isLoadingList,
        itemsLoadedAt: state[reducer].itemsLoadedAt,
        theme: state.layout.theme,
        isLoadingCount: state[reducer].isLoadingCount,
        shouldUpdate: state[reducer].shouldUpdate,
        enums: state.dictionaries.enums,
        roles: state.profile.roles,
        canCreateBids: state.profile.profile.canCreateBids,
        canEditBooks: policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book: reducer },
        }).canEditBook(),
        canCreateBooks: policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book: reducer },
        }).canCreateBook(),
        organizationTimezone: state.profile.timezone,
        organization: state.profile.organization,
        profile: state.profile.profile,
        refuelingSettings: state.profile.refuelingSettings,
    };
}

export function tableDispatchToProps(
    dispatch: ActionFunctionAny<Action<Object>>,
    actions: {
        index: (params: {}) => void;
        merge: (params: {}) => void;
        replace: (params: {}) => void;
        open: (params: {}) => void;
        clear: (params: {}) => void;
    },
) {
    return {
        getList: (params: {}) => dispatch(actions.index(params)),
        mergeParams: (params: {}) => dispatch(actions.merge(params)),
        replaceParams: (params: {}) => dispatch(actions.replace(params)),
        modal: (params: {}) => dispatch(modalActions.open(params)),
        clearParams: (params = {}) =>
            dispatch(actions.clear({ filters: [], start: 0, customFilters: {}, preparedFilter: {}, ...params })),
    };
}

export function tableContainer<T>(Component: FC, reducer: string) {
    return connect(
        (state: any) => tableStateToProps<T>(state, reducer),
        (dispatch) => tableDispatchToProps(dispatch, generatedActions[reducer]),
    )(Component);
}

export function viewStateToProps(
    state: {
        profile: {
            roles: RolesModel;
            profile: { canCreateBids: boolean; isDemoPeriodExpired?: boolean };
            organization: OrganizationProfileModel;
            timezone: string;
            refuelingSettings: {};
        };
        dictionaries: { enums: []; items: [] };
        layout: { data: {}; theme: string };
        configs: { tileUrls: [] };
        bgTasks: { items: [] };
    },
    reducer: string,
) {
    return {
        roles: state.profile.roles,
        profile: state.profile.profile,
        enums: state.dictionaries.enums,
        tmpData: state.layout.data,
        dictionaries: state.dictionaries.items,
        theme: state.layout.theme,
        tileUrls: state.configs.tileUrls,
        canEditBooks: policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book: reducer },
        }).canEditBook(),
        canCreateBooks: policy("user", {
            user: {
                canEditAll: !state.profile.organization.noAccessToEditBooks,
                intBooksEditPermissionsFlags: state.profile.organization.intBooksEditPermissionsFlags,
                roles: state.profile.roles,
                isDemoPeriodExpired: state.profile.profile?.isDemoPeriodExpired,
            },
            record: {},
            options: { book: reducer },
        }).canCreateBook(),
        organizationTimezone: state.profile.timezone,
        organization: state.profile.organization,
        bgTasks: state.bgTasks.items,
        refuelingSettings: state.profile.refuelingSettings,
    };
}

export function viewDispatchToProps(
    dispatch: ActionFunctionAny<Action<Object>>,
    actions: {
        index: (params: {}) => void;
        merge: (params: {}) => void;
        replace: (params: {}) => void;
        open: (params: {}) => void;
    },
) {
    return {
        modal: (params: {}) => dispatch(modalActions.open(params)),
        updateModal: (params: {}) => dispatch(modalActions.update(params)),
        addModal: (params: {}) => dispatch(modalActions.add(params)),
        putData: (params: {}) => dispatch(layoutActions.putData(params)),
        clearData: () => dispatch(layoutActions.clearData()),
        mergeMapData: (data: {}) => dispatch(mapActions.mergeMapData(data)),
        addBgTask: (data: {}) => dispatch(bgTasksActions.add(data)),
        removeBgTask: (data: {}) => dispatch(bgTasksActions.remove(data)),
        removeAllBgTasks: () => dispatch(bgTasksActions.removeAll()),
        switchMap: (params: {}) => dispatch(mapActions.switchMap(params)),
        ...(actions ? { mergeParams: (params: {}) => dispatch(actions.merge(params)) } : {}),
    };
}

export function viewContainer(Component: FC, book: string) {
    return connect(
        (state: any) => viewStateToProps(state, book),
        (dispatch) => viewDispatchToProps(dispatch, generatedActions[book]),
    )(Component);
}

export function chatContainer(Component: FC) {
    return connect(
        (state: {
            profile: {
                roles: [];
                profile: { canCreateBids: boolean };
                organization: OrganizationProfileModel;
                timezone: string;
                token: string;
            };
            layout: { theme: string };
        }) => {
            return {
                roles: state.profile.roles,
                profile: state.profile.profile,
                token: state.profile.token,
                theme: state.layout.theme,
                organization: state.profile.organization,
                organizationTimezone: state.profile.timezone,
            };
        },
        (dispatch) => ({
            modal: (params: {}) => dispatch(modalActions.open(params) as unknown as Action<any>),
            addModal: (params: {}) => dispatch(modalActions.add(params) as unknown as Action<any>),
            mergeChatsParams: (params: {}) => dispatch(generatedActions.chats.merge(params) as unknown as Action<any>),
            mergeBidsParams: (params: {}) => dispatch(generatedActions.bids.merge(params)),
        }),
    )(Component);
}

export function themeContainer(Component: any) {
    return connect((state: { layout: { theme: string } }) => ({ theme: state.layout.theme }))(Component);
}
